import React, { Suspense } from 'react'
import PropTypes from 'prop-types'
import { lazyWithRetry } from 'utils/lazyWithRetry'
import { Loading } from 'components/patterns'
import { COMPONENT } from 'utils/constants'
import styled from '@emotion/styled'
import {
  AuthorizationError,
  NotFoundError,
  DefaultError,
} from 'components/errorPageContent'
import useShuffler from 'hooks/useShuffler'

const CodedErrors = {
  401: AuthorizationError,
  404: NotFoundError,
  0: DefaultError,
  500: DefaultError,
  504: DefaultError,
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      error: '',
      info: '',
    }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    this.makeError(error, info)
  }

  componentDidUpdate() {
    const { error } = this.props
    const { error: stateErr } = this.state

    if (
      error &&
      (!this.state.error ||
        (error.code &&
          stateErr &&
          stateErr.code &&
          error.code !== stateErr.code))
    ) {
      this.makeError(error)
    } else if (!error && stateErr && stateErr.code) {
      this.setState({ error: '', info: '' })
    }
  }

  makeError = (error, info) => {
    // You can also log the error to an error reporting service
    this.setState({ error, info })
  }

  render() {
    const { level, isWaiting } = this.props
    const { error } = this.state
    const { code = 0 } = error
    const CodeErrorComponent = CodedErrors[code]
    if (error) {
      return (
        <Wrapper>
          <Illustration />
          <Container>
            <CodeErrorComponent
              message={error.errorMessage}
              level={level}
              error={error}
            />
          </Container>
        </Wrapper>
      )
    }

    return isWaiting ? <Loading contained /> : this.props.children
  }
}

export default ErrorBoundary

ErrorBoundary.defaultProps = {
  level: COMPONENT,
}

ErrorBoundary.propTypes = {
  /**
   Error Boundary for all cases
    https://reactjs.org/docs/error-boundaries.html
   */
  /** Component level being captured. Defaults to COMPONENT */
  level: PropTypes.oneOf(['app', 'component']),
  /** The component being wrapped */
  children: PropTypes.node,
  /** History object off Router */
  history: PropTypes.object,
  /** pending async call */
  isWaiting: PropTypes.bool,
  /** relevant async call has errored  */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
}

const Container = styled.div`
  margin-left: 20px;
  width: 511px;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Image = styled.div`
  width: 365px;
  height: 350px;
`

const Shuffler = ({ items }) => {
  const [Cursor] = useShuffler(items)
  return (
    <Suspense fallback={<></>}>
      <Cursor />
    </Suspense>
  )
}

Shuffler.propTypes = {
  items: PropTypes.node,
}

export const Illustration = () => (
  <Image>
    <Shuffler
      items={[
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/ClawMachine')
        ),
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/Desert')
        ),
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/Kraken')
        ),
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/TarPit')
        ),
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/UFO')
        ),
        lazyWithRetry(() =>
          import('components/errorPageContent/illustrations/UnderWater')
        ),
      ]}
    />
  </Image>
)
